import React from 'react'
import { AlertTriangle, Cpu, CircleSlash, FileText } from 'lucide-react'

/* ======================================================================
 * WHAT PUBLISHING THIS DEFINITION ACTUALLY COMMITS (DECISIONS01 A1)
 * ======================================================================
 *
 * ONE COMPONENT, RENDERING A PAYLOAD THE BACKEND SENDS. The library
 * panel and the builder's Publish button both draw this, and neither
 * restates any of it in JSX. Two screens describing the same act in
 * their own words is how they end up describing it differently, and the
 * screen that gets it wrong is the one with the Publish button on it.
 *
 * The precedent is written down in this repo already:
 * AdminModulesPage.jsx opens by recording that it is the SECOND OF FOUR
 * hardcoded copies of the module catalogue with nothing reconciling
 * them. A fifth copy — of the sentence that tells an admin whether they
 * are rewiring onboarding — would be that mistake with much worse
 * consequences.
 *
 * So this file contains no claims. wf_engine/routes.py:_publish_effect()
 * contains them, varying by execution_owner, and this decides only what
 * they look like.
 * ====================================================================== */

const STATUS = {
  // The four states a published field can be in, most consequential
  // first. `live` is deliberately absent: nothing in the payload claims
  // it today, and inventing a badge for a state nothing returns is how a
  // vocabulary starts describing something that does not exist.
  armed: {
    label: 'Armed',
    cls: 'border-amber-400/50 bg-amber-400/10 text-amber-200',
  },
  enforced: {
    label: 'Enforced',
    cls: 'border-amber-400/50 bg-amber-400/10 text-amber-200',
  },
  recorded: {
    label: 'Recorded',
    cls: 'border-sky-400/40 bg-sky-400/10 text-sky-200',
  },
  documentation: {
    label: 'Documentation',
    cls: 'border-zinc-600 bg-zinc-700/20 text-zinc-300',
  },
}

const OWNER = {
  code: {
    icon: FileText,
    cls: 'border-sky-400/40 bg-sky-400/5 text-sky-100',
  },
  engine: {
    icon: Cpu,
    cls: 'border-amber-400/50 bg-amber-400/5 text-amber-100',
  },
  none: {
    icon: CircleSlash,
    cls: 'border-zinc-600 bg-zinc-700/20 text-zinc-300',
  },
}

/**
 * @param {object}  effects   the `validation.publish_commits` payload
 * @param {boolean} prominent the builder passes true — the headline
 *                            becomes a banner rather than a line, because
 *                            this is the screen where somebody is most
 *                            likely to believe they are rewiring
 *                            onboarding.
 */
export default function PublishEffects({ effects, prominent = false }) {
  if (!effects) return null
  const owner = OWNER[effects.execution_owner] || OWNER.none
  const Icon = owner.icon

  return (
    <div className="space-y-2">
      <h3 className="text-xs uppercase tracking-wide text-zinc-500">
        Publishing this commits
      </h3>

      <div
        className={`flex gap-2 rounded border p-2 text-xs leading-relaxed ${owner.cls} ${
          prominent ? 'text-[12.5px]' : ''
        }`}
      >
        <Icon size={14} className="mt-0.5 shrink-0" />
        <span>{effects.headline}</span>
      </div>

      <dl className="space-y-1">
        {(effects.rows || []).map((row) => {
          const status = STATUS[row.status] || STATUS.documentation
          return (
            <div
              key={row.field}
              className="rounded border border-zinc-800 bg-black/20 p-2"
            >
              <dt className="flex items-center justify-between gap-2">
                <span className="text-xs text-zinc-300">{row.label}</span>
                <span
                  className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${status.cls}`}
                >
                  {status.label}
                </span>
              </dt>
              <dd className="mt-1 text-[11.5px] leading-relaxed text-zinc-500">
                {row.effect}
              </dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}

/** The publish blockers, listed with the rule that produced each.
 *
 *  SHARED WITH THE LIBRARY, WHICH SHOWS THEM AS A LIST. The builder also
 *  draws each of these on the step it names — see the builder's
 *  toneOf/anchoring — because a finding that is only in a summary makes
 *  the reader hunt for the field it is about. This list is the summary
 *  half; it does not replace the anchoring. */
export function BlockingFindings({ findings, onJumpToStep }) {
  if (!findings || findings.length === 0) return null
  return (
    <div className="space-y-1">
      <h3 className="flex items-center gap-1 text-xs uppercase tracking-wide text-amber-400">
        <AlertTriangle size={12} /> Blocks publishing ({findings.length})
      </h3>
      <ul className="space-y-1">
        {findings.map((f, i) => (
          <li
            key={`${f.rule}-${f.step_key || i}`}
            className={`rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200/90 ${
              f.step_key && onJumpToStep ? 'cursor-pointer hover:bg-amber-500/10' : ''
            }`}
            onClick={
              f.step_key && onJumpToStep ? () => onJumpToStep(f.step_key) : undefined
            }
          >
            <span className="font-mono text-[10px] text-amber-400">{f.rule}</span>{' '}
            {f.message}
            {f.step_key && (
              <span className="text-amber-300/70"> — step “{f.step_key}”</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
